/**
 * Navbar Component
 * 
 * Features:
 * - Responsive navigation with mobile menu
 * - Sticky positioning with backdrop blur
 * - Brand logo with gradient text
 * - Desktop and mobile navigation links
 * - Clears localStorage on logo click for event flow reset
 * 
 * Layout:
 * - Desktop: Logo on left, navigation links on right
 * - Mobile: Logo on left, hamburger menu on right with expandable panel
 * 
 * Functionality:
 * - Tracks mobile menu open/close state
 * - Handles logo click to reset event planning flow
 * - Closes mobile menu when link is clicked
 * 
 * Styling:
 * - Semi-transparent white background with blur
 * - Purple gradient text for brand
 * - Hover and focus states for interactive elements
 * - Smooth transitions for menu interactions
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ScrollToTopLink from './ScrollToTopLink';
import { useEventForm } from '../context/EventFormContext';

/* Commented out for now - can be used for dynamic navigation in the future
const navigationItems = [
  { name: 'Home', href: '/' },
  { name: 'Mission', href: '/mission' },
  { name: 'Venues', href: '/venues' },
  { name: 'Calculator', href: '/calculator' },
  // ... any other existing nav items ...
];
*/

export function Navbar({ transparent = false }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { clearFormData } = useEventForm();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = () => {
    clearFormData();
  };

  const isActive = (path) => location.pathname === path;

  return (
    <motion.nav 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        transparent && !scrolled
          ? 'bg-transparent'
          : scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
        <div className="flex items-center justify-between h-20">
          <ScrollToTopLink to="/" className="flex items-center" onClick={handleLogoClick}>
            <motion.img 
              src={transparent && !scrolled ? "/letshost-logo-white.png" : "/letshost-logo-black.png"}
              alt="LetsHost"
              className="h-10 transform-gpu" 
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
              style={{ willChange: 'transform', backfaceVisibility: 'hidden' }}
            />
          </ScrollToTopLink>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-10">
            {[
              { to: '/mission', label: 'Mission' },
              { to: '/venues', label: 'Venues' },
              { to: '/calculator', label: 'Calculator' },
              { to: '/inspo', label: 'Inspo' },
              { to: '/guides', label: 'Guides' },
            ].map(({ to, label }) => {
              const isTransparentMode = transparent && !scrolled;
              return (
                <ScrollToTopLink
                  key={to}
                  to={to}
                  className={`relative px-2 py-1 text-base font-medium transition-colors duration-200 ${
                    isActive(to)
                      ? isTransparentMode ? 'text-white' : 'text-stone-800'
                      : isTransparentMode ? 'text-white/80 hover:text-white' : 'text-stone-500 hover:text-stone-800'
                  }`}
                >
                  {label}
                  {isActive(to) && (
                    <motion.span
                      className={`absolute bottom-0 left-0 w-full h-0.5 ${
                        isTransparentMode ? 'bg-white' : 'bg-stone-800'
                      }`}
                      layoutId="navbar-underline"
                    />
                  )}
                </ScrollToTopLink>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center">
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`inline-flex items-center justify-center p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-inset transition-colors duration-200 ${
                transparent && !scrolled
                  ? 'text-white hover:text-white/80 hover:bg-white/10 focus:ring-white'
                  : 'text-stone-500 hover:text-stone-800 hover:bg-stone-50 focus:ring-stone-500'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            className="md:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-100 rounded-b-2xl shadow-xl"
              initial={{ y: -20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <ScrollToTopLink
                to="/mission"
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                  isActive('/mission') 
                    ? 'text-stone-800 bg-stone-100' 
                    : 'text-stone-600 hover:text-stone-800 hover:bg-stone-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Mission
              </ScrollToTopLink>
              <ScrollToTopLink
                to="/venues"
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                  isActive('/venues') 
                    ? 'text-stone-800 bg-stone-100' 
                    : 'text-stone-600 hover:text-stone-800 hover:bg-stone-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Venues
              </ScrollToTopLink>
              <ScrollToTopLink
                to="/calculator"
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                  isActive('/calculator')
                    ? 'text-stone-800 bg-stone-100' 
                    : 'text-stone-600 hover:text-stone-800 hover:bg-stone-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Calculator
              </ScrollToTopLink>
              <ScrollToTopLink
                to="/inspo"
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                  isActive('/inspo')
                    ? 'text-stone-800 bg-stone-100' 
                    : 'text-stone-600 hover:text-stone-800 hover:bg-stone-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Inspo
              </ScrollToTopLink>
              <ScrollToTopLink
                to="/guides"
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                  isActive('/guides')
                    ? 'text-stone-800 bg-stone-100' 
                    : 'text-stone-600 hover:text-stone-800 hover:bg-stone-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Guides
              </ScrollToTopLink>
              {/* <ScrollToTopLink
                to="/guestlist"
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                  isActive('/guestlist') 
                    ? 'text-indigo-600 bg-indigo-50' 
                    : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Guestlist
              </ScrollToTopLink> */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}